"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";
import { isComedianPublished, isPlaceholderComedian } from "@/data/comedians";

import Link from "next/link";
import { cn } from "@punchup/utils/cn";
import { useShowRowContext } from "@/components/shows/ShowRowContext";

interface ShowRowComedianNameProps {
  showTitle?: string;
  className?: string;
  withTooltip?: boolean;
}

export function ShowRowComedianName({
  showTitle,
  className,
  withTooltip = false,
}: ShowRowComedianNameProps) {
  const {
    comedian,
    isTicketmasterEvent,
    handleComedianNameClick: onComedianNameClick,
    showcaseComedians,
  } = useShowRowContext();

  // Showcase mode: show title + comma-separated comedian names (always with tooltips)
  if (showcaseComedians && showcaseComedians.length > 1) {
    return (
      <>
        {showTitle && (
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <p className="text-[16px] font-semibold leading-tight line-clamp-1">
                  {showTitle}
                </p>
              </TooltipTrigger>
              <TooltipContent sideOffset={5} className="max-w-xs text-sm">
                {showTitle}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <p className="text-[14px] text-secondary-blue leading-tight line-clamp-1">
                {showcaseComedians
                  .slice(0, 2)
                  .map((c) => c.display_name)
                  .join(", ")}
                {showcaseComedians.length > 2 && (
                  <span> +{showcaseComedians.length - 2}</span>
                )}
              </p>
            </TooltipTrigger>
            <TooltipContent sideOffset={5} className="max-w-xs text-sm">
              {showcaseComedians.map((c) => c.display_name).join(", ")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </>
    );
  }

  // Single comedian mode: Link / plain text / button
  const isLinkable =
    comedian.about_img !== null &&
    !isTicketmasterEvent &&
    !isPlaceholderComedian(comedian.display_name || "") &&
    isComedianPublished(comedian);

  const isPlainText =
    isTicketmasterEvent ||
    isPlaceholderComedian(comedian.display_name || "") ||
    !isComedianPublished(comedian);

  const element = isLinkable ? (
    <Link
      href={`/${comedian.slug}`}
      className={cn(className, "hover:underline underline-offset-2")}
      onClick={(e) => e.stopPropagation()}
    >
      {comedian.display_name}
    </Link>
  ) : isPlainText ? (
    <p className={className}>{comedian.display_name}</p>
  ) : (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onComedianNameClick(comedian);
      }}
      className={cn(className, "text-left hover:underline underline-offset-2")}
    >
      {comedian.display_name}
    </button>
  );

  if (withTooltip) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>{element}</TooltipTrigger>
          <TooltipContent sideOffset={5} className="max-w-xs text-sm">
            {comedian.display_name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return element;
}
